import { assertString } from '@Utils/assert';
import { fromJS } from 'immutable';

import { byValue, ascending, descending, occurrence } from '@Containers/ResultsFilters/ResultsFiltersConstants';

import { normalizeItems, findByIdForImmutableCollectionFn } from '@Utils';
import {
  updateCustomGroupsByIdType,
  updateChunksFilterByIdType,
  syncAllChunksWithGroupsType,
  deselectAllChunksOfFilterType,
  selectAllChunksOfFilterType,
  updateGroupByIdInChunksOfFilterType,
} from '../ListOfFiltersConstants';

import initialState from './initialState';

function createFnSetSelectedAs(val) {
  return function mapSetSelectedAs(item) {
    return item.set('selected', val);
  };
}

function mapGroupsInAllChunks(state, key, filterId, updater) {
  const updateChunkItems = (items) => items.map(updater);

  const updateChunks = (chunks) => {
    if (!chunks) {
      return chunks;
    }

    return chunks.map((chunk) => chunk.update('items', updateChunkItems));
  };

  return state
    .updateIn([key, 'items', filterId, 'chunks', occurrence, ascending], updateChunks)
    .updateIn([key, 'items', filterId, 'chunks', occurrence, descending], updateChunks)
    .updateIn([key, 'items', filterId, 'chunks', byValue, ascending], updateChunks)
    .updateIn([key, 'items', filterId, 'chunks', byValue, descending], updateChunks);
}

// eslint-disable-next-line import/prefer-default-export,default-param-last
export function barChartFiltersReducer(state = initialState, action) {
  switch (action.type) {
    case updateCustomGroupsByIdType: {
      const { key, filterId, data } = action.payload;

      return state.updateIn([key, 'items', filterId], (filter) => {
        const { chunks } = data;
        return filter.set('customGroupsData', chunks);
      });
    }
    case updateChunksFilterByIdType: {
      const {
        key,
        filterId,
        sortingType,
        sorting,
        data,
        chunkIndex,
        letter,
        customGroupsWithByValueSorting,
        isSortingChanged,
        isSortingTypeChanged,
      } = action.payload;
      if (customGroupsWithByValueSorting) {
        assertString(letter, 'letter');
      } else {
        assertString(chunkIndex, 'chunkIndex');
      }
      return state.updateIn([key, 'items', filterId], (filter) => {
        const { groupCount, chunksCount, chunks } = data;

        const { ids, items } = normalizeItems(chunks);
        let resetChunk = filter;
        if (isSortingChanged || isSortingTypeChanged) {
          resetChunk = filter.has('clearGroupValues') ? filter.set('clearGroupValues', fromJS(null)) : filter;
        }
        if (customGroupsWithByValueSorting) {
          return resetChunk
            .set('chunksCount', chunksCount)
            .set('groupCount', groupCount)
            .setIn(['chunks', sortingType, sorting, letter, 'items'], fromJS(items))
            .setIn(['chunks', sortingType, sorting, letter, 'ids'], fromJS(ids));
        }
        return resetChunk
          .set('chunksCount', chunksCount)
          .set('groupCount', groupCount)
          .setIn(['chunks', sortingType, sorting, chunkIndex, 'items'], fromJS(items))
          .setIn(['chunks', sortingType, sorting, chunkIndex, 'ids'], fromJS(ids));
      });
    }

    case updateGroupByIdInChunksOfFilterType: {
      const { key, data, groupId, filterId } = action.payload;

      return mapGroupsInAllChunks(state, key, filterId, (item) => {
        if (item.get('id') !== groupId) {
          return item;
        }

        return item.merge(fromJS(data));
      });
    }

    case deselectAllChunksOfFilterType: {
      const { key, filterId } = action.payload;
      /** added additional obj for to ensure dialog changes are not impacting the filter groups */
      let updatedList = mapGroupsInAllChunks(state, key, filterId, createFnSetSelectedAs(false));
      updatedList = updatedList.updateIn([key, 'items', filterId], (item) =>
        item.set(
          'clearGroupValues',
          item.get('groups').map((group) => group.set('selected', false))
        )
      );
      return updatedList;
    }

    case selectAllChunksOfFilterType: {
      const { key, filterId } = action.payload;
      /** added additional obj for to ensure dialog changes are not impacting the filter groups */
      let updatedList = mapGroupsInAllChunks(state, key, filterId, createFnSetSelectedAs(true));
      updatedList = updatedList.updateIn([key, 'items', filterId], (item) =>
        item.set(
          'clearGroupValues',
          item.get('groups').map((group) => group.set('selected', true))
        )
      );
      return updatedList;
    }

    case syncAllChunksWithGroupsType: {
      const { key, filterId } = action.payload;
      const groups = state.getIn([key, 'items', filterId, 'groups']);
      const selectedItemsFromDialog = state.getIn([key, 'items', filterId, 'selectedItemsFromDialog']);

      if (selectedItemsFromDialog) {
        return mapGroupsInAllChunks(state, key, filterId, (item) => {
          const id = item.get('id');
          const selected = !!selectedItemsFromDialog.find((selectedItem) => selectedItem.get('id') === id);
          return item.set('selected', selected);
        });
      }

      return mapGroupsInAllChunks(state, key, filterId, (item) => {
        const id = item.get('id');
        const group = groups.find(findByIdForImmutableCollectionFn(id));
        const selected = group ? group.get('selected') : false;

        return item.set('selected', selected);
      });
    }

    default: {
      return state;
    }
  }
}
